import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import MemberRow from './MemberRow';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { AppContext } from 'src/store/ContextStore';
import { isAnonymousOrLoading } from '../anonymousPedigrees';

const LS_KEY = 'bulk-edit-visible-columns';

// Keep in sync with useBulkEditState — only used as a fallback default here
function emptyEntry() {
  return {
    genderIdentity: null,
    isAlive: null,
    dob: null,
    dod: null,
    firstName: null,
    lastName: null,
    ethnicity: null,
    mobile: null,
    email: null,
    hospitalNumber: null,
    healthcareId: null,
    isHide: null,
    recordsReviewed: null,
    newDiseases: [],
    removedDiseaseIds: [],
    newPhenotypes: [],
    removedPhenotypeIds: [],
    newGenotypes: [],
    removedGenotypeIds: []
  };
}

const ALL_COLUMNS = [
  { id: 'firstName', label: 'First name', defaultVisible: true },
  { id: 'lastName', label: 'Last name', defaultVisible: true },
  { id: 'gender', label: 'Gender', width: 110, defaultVisible: true },
  { id: 'status', label: 'Status', width: 110, defaultVisible: true },
  { id: 'dob', label: 'Date of birth', width: 130, defaultVisible: true },
  { id: 'dod', label: 'Date of death', width: 130, defaultVisible: true },
  { id: 'ethnicity', label: 'Ethnicity', width: 180, defaultVisible: false },
  { id: 'mobile', label: 'Mobile', defaultVisible: false },
  { id: 'email', label: 'Email', defaultVisible: false },
  {
    id: 'hospitalNumber',
    label: 'Hospital Number',
    defaultVisible: false
  },
  { id: 'healthcareId', label: 'Healthcare ID', defaultVisible: false },
  {
    id: 'isHide',
    label: 'Hide on Pedigree',
    width: 120,
    defaultVisible: false
  },
  {
    id: 'recordsReviewed',
    label: 'Records Reviewed',
    width: 130,
    defaultVisible: false
  }
];

const PII_COLUMN_IDS = [
  'firstName',
  'lastName',
  'dob',
  'dod',
  'mobile',
  'email',
  'hospitalNumber',
  'healthcareId'
];

function loadVisibility() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return Object.fromEntries(ALL_COLUMNS.map((c) => [c.id, c.defaultVisible]));
}

export default function MemberTable({
  memberRecords,
  draft,
  isDirty,
  actions,
  familyId
}) {
  const { content } = useContext(AppContext);
  const anonymousPedigrees = isAnonymousOrLoading(
    content?.settings?.pedigreeSetting
  );
  const [visibility, setVisibility] = useState(loadVisibility);
  const [anchorEl, setAnchorEl] = useState(null);
  const [ethnicityOptions, setEthnicityOptions] = useState([]);

  useEffect(() => {
    axios
      .get(APICONSTANTS.getEthnicity)
      .then((res) => {
        const opts = res.data;
        if (Array.isArray(opts)) {
          setEthnicityOptions(opts.map((e) => (e.name ? e.name : e)));
        }
      })
      .catch(() => {});
  }, []);

  const toggleColumn = (id) => {
    setVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Deriving from the clamped set every render keeps stale localStorage
  // visibility entries from re-adding PII columns in anonymous mode.
  const availableColumns = anonymousPedigrees
    ? ALL_COLUMNS.filter((c) => !PII_COLUMN_IDS.includes(c.id))
    : ALL_COLUMNS;
  const visibleColumns = availableColumns.filter((c) => visibility[c.id]);
  // colSpan for expanded row: expand toggle + UPN + visible cols + dirty dot
  const colSpan = 2 + visibleColumns.length + 1;

  return (
    <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
      <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            {/* Expand toggle */}
            <TableCell
              sx={{
                width: 40,
                py: 1,
                bgcolor: 'grey.100',
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}
            />
            {/* UPN */}
            <TableCell
              sx={{
                width: 60,
                py: 1,
                bgcolor: 'grey.100',
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary'
                }}
              >
                UPN
              </Typography>
            </TableCell>

            {visibleColumns.map((col) => (
              <TableCell
                key={col.id}
                sx={{
                  width: col.width,
                  py: 1,
                  bgcolor: 'grey.100',
                  borderBottom: '2px solid',
                  borderBottomColor: 'divider'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary'
                  }}
                >
                  {col.label}
                </Typography>
              </TableCell>
            ))}

            {/* Columns filter button in last header cell */}
            <TableCell
              sx={{
                width: 40,
                py: 1,
                bgcolor: 'grey.100',
                borderBottom: '2px solid',
                borderBottomColor: 'divider',
                pr: 0.5
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{ minWidth: 0, p: 0.5 }}
                  title="Show/hide columns"
                >
                  <ViewColumnIcon fontSize="small" />
                </Button>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {memberRecords.map((member, index) => (
            <MemberRow
              key={member.pid}
              member={member}
              index={index}
              entry={draft.get(member.pid) || emptyEntry()}
              isDirty={isDirty(member.pid)}
              actions={actions}
              familyId={familyId}
              visibleColumns={visibleColumns}
              ethnicityOptions={ethnicityOptions}
              colSpan={colSpan}
            />
          ))}
        </TableBody>
      </Table>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.5, minWidth: 200 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              display: 'block',
              mb: 0.5
            }}
          >
            Show / hide columns
          </Typography>
          {availableColumns.map((col) => (
            <Box
              key={col.id}
              sx={{
                display: 'block'
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(visibility[col.id])}
                    onChange={() => toggleColumn(col.id)}
                  />
                }
                label={<Typography variant="body2">{col.label}</Typography>}
              />
            </Box>
          ))}
        </Box>
      </Popover>
    </TableContainer>
  );
}
